import { Track } from 'livekit-client';
import type { LocalTrackPublication, LocalVideoTrack } from 'livekit-client';

/**
 * Pure, framework-free screen-share helpers so the UI decisions can be unit
 * tested without a LiveKit Room or a DOM. The track-lifecycle wiring lives in
 * useCallRoom; the layout lives in CallView.
 */

/** True when the current browser can capture a screen via getDisplayMedia.
 *  iOS Safari and most mobile browsers return false, so the share button is
 *  hidden there — receiving a shared screen still works everywhere. */
export function screenCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

/** Whether to render the local "share screen" button. Hidden when capture is
 *  unsupported and — to enforce one-share-at-a-time — when the remote peer is
 *  already sharing. Stays visible while WE share so the user can stop. */
export function shouldShowShareButton(args: {
  supported: boolean;
  remoteSharing: boolean;
}): boolean {
  return args.supported && !args.remoteSharing;
}

/** Find the published local screen-share video track among a participant's
 *  video publications, or null if none is live yet. Pulled out of useCallRoom
 *  so the picker-cancel branch (setScreenShareEnabled resolves with no track)
 *  is unit-testable. */
export function findScreenShareTrack(
  pubs: Iterable<LocalTrackPublication>,
): LocalVideoTrack | null {
  for (const pub of pubs) {
    if (pub.source === Track.Source.ScreenShare && pub.track) {
      return pub.track as LocalVideoTrack;
    }
  }
  return null;
}
